import React, { useState, useRef } from "react";
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Button,
  Box,
  Alert,
  ColumnLayout,
  Badge,
  ProgressBar,
  RadioGroup,
  ExpandableSection,
} from "@cloudscape-design/components";
import { icFileApi } from "./ListingCreationApi.js";

const MAX_FILE_SIZE = 10485760; // 10 MB

const PLATFORM_OPTIONS = [
  { value: "shopify", label: "Shopify" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "shopdeck", label: "Shopdeck" },
  { value: "wix", label: "Wix — Coming Soon", disabled: true },
];

// Map log content to progress percentage
function getProgressFromLogs(logs) {
  if (!logs || logs === "Waiting for logs...") return 5;
  if (logs.includes("Done!")) return 100;
  if (logs.includes("Writing Excel")) return 90;
  if (logs.includes("rows built")) return 80;
  if (logs.includes("Building SmartBiz")) return 70;
  if (logs.includes("Categories assigned")) return 65;
  if (logs.includes("Classifying")) return 40;
  if (logs.includes("Images checked")) return 32;
  if (logs.includes("Checking image formats")) return 31;
  if (logs.includes("products extracted")) return 30;
  if (logs.includes("Converting products")) return 25;
  if (logs.includes("Columns validated")) return 20;
  if (logs.includes("CSV loaded")) return 15;
  if (logs.includes("Reading")) return 10;
  return 5;
}

export default function IcFileConverter({ username, onConversionComplete }) {
  const [platform, setPlatform] = useState(null);
  const [file, setFile] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [runStatus, setRunStatus] = useState(null);
  const [logs, setLogs] = useState("Waiting for logs...");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // Pre-flight image-format scan result. Non-blocking — used to surface a
  // "we'll auto-fix WebP images" notice before the user commits to the run.
  const [sniffResult, setSniffResult] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = (selectedFile) => {
    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      setError("Please upload a .csv file");
      return false;
    }
    if (selectedFile.size >= MAX_FILE_SIZE) {
      setError("File size must be less than 10 MB");
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const selectedFile = e.dataTransfer.files?.[0];
    if (!selectedFile) return;
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      setError(null);
    } else {
      setFile(null);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleZoneClick = () => { if (platform) fileInputRef.current?.click(); };

  const handleConvert = async () => {
    setError(null);

    if (!platform) {
      setError("Please select a platform");
      return;
    }
    if (!file) {
      setError("Please select a CSV file to upload");
      return;
    }

    setIsConverting(true);
    setRunStatus({ status: "uploading" });

    try {
      // Step 1: Get presigned upload URL
      const { run_id, upload_url } = await icFileApi.getUploadUrl(
        file.name,
        username,
        platform
      );

      setRunStatus({ status: "uploading", run_id });

      // Step 2: Upload CSV directly to S3 via presigned URL
      await icFileApi.uploadFileToS3(upload_url, file);

      setRunStatus({ status: "processing", run_id });

      // Step 2.5: Pre-flight image format scan. Non-blocking — sets a banner
      // when WebP / unsupported formats are detected so the seller knows we'll
      // auto-convert (and the run may take a bit longer because of that).
      try {
        const sniff = await icFileApi.sniffImages(run_id);
        if (sniff && sniff.needs_conversion) {
          setSniffResult(sniff);
        } else {
          setSniffResult(null);
        }
      } catch (e) {
        // Sniff is purely advisory — never let it block conversion.
        setSniffResult(null);
      }

      // Step 3: Trigger conversion (async — returns immediately)
      await icFileApi.startConversion(run_id);

      // Step 4: Poll for completion + logs
      let attempts = 0;
      while (attempts < 60) {
        await new Promise((r) => setTimeout(r, 3000));
        attempts++;

        // Fetch logs
        try {
          const logData = await icFileApi.getLogs(run_id);
          const logText = logData.logs || "Waiting for logs...";
          setLogs(logText);
          setProgress(getProgressFromLogs(logText));
        } catch (e) { /* ignore log fetch errors */ }

        // Fetch status
        const status = await icFileApi.getStatus(run_id);
        setRunStatus(status);
        if (status.status === "completed" || status.status === "failed") {
          setProgress(status.status === "completed" ? 100 : 0);
          if (onConversionComplete) onConversionComplete();
          break;
        }
      }
    } catch (err) {
      setError(err.message);
      setRunStatus({ status: "failed" });
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownload = async (runId) => {
    try {
      await icFileApi.downloadOutput(runId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = () => {
    setFile(null);
    setRunStatus(null);
    setError(null);
    setLogs("Waiting for logs...");
    setProgress(0);
    setSniffResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getStatusBadge = (status) => {
    const map = {
      uploading: "blue",
      processing: "blue",
      completed: "green",
      failed: "red",
    };
    return <Badge color={map[status] || "grey"}>{status.toUpperCase()}</Badge>;
  };

  const platformLabel = PLATFORM_OPTIONS.find((o) => o.value === platform)?.label || "—";

  return (
    <SpaceBetween size="l">
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload & Config Section */}
      <Container header={<Header variant="h2">Convert IC File to SmartBiz</Header>}>
        <SpaceBetween size="m">
          <Alert type="info">
            Select your e-commerce platform and upload your product export CSV.
            The tool will convert it to SmartBiz bulk upload format with automatic category classification.
          </Alert>

          <FormField
            label="Source Platform"
            description="Select the platform your CSV was exported from"
          >
            <RadioGroup
              onChange={({ detail }) => setPlatform(detail.value)}
              value={platform}
              items={PLATFORM_OPTIONS}
            />
          </FormField>

          <Box margin={{ top: "s" }}>
            <Box variant="awsui-key-label" margin={{ bottom: "xxs" }}>Upload Product CSV</Box>
            <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: "xs" }}>CSV file exported from your platform (max 10 MB)</Box>
            <div
              role="button"
              tabIndex={0}
              className={`image-link-drop-zone ${dragOver ? "drag-over" : ""} ${!platform ? "disabled" : ""}`}
              onClick={handleZoneClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleZoneClick(); }}
              aria-label="Drop CSV here or click to browse"
            >
              <p className="drop-zone-icon">📄</p>
              <p className="drop-zone-title">
                {file ? file.name : "Drag & drop your CSV here, or click to select"}
              </p>
              <p className="drop-zone-hint">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : "Shopify, WooCommerce, or Shopdeck product export CSV"}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>

          <Button
            variant="primary"
            loading={isConverting}
            onClick={handleConvert}
            disabled={!file || !platform}
          >
            🔄 Convert to SmartBiz Format
          </Button>
        </SpaceBetween>
      </Container>

      {/* Conversion Status */}
      {runStatus && (
        <Container header={<Header variant="h2">⚙️ Conversion Result</Header>}>
          <SpaceBetween size="m">
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <div>{getStatusBadge(runStatus.status)}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Platform</Box>
                <Box>{platformLabel}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Rows Generated</Box>
                <Box>{runStatus.total_rows ?? "—"}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">File</Box>
                <Box>{file?.name || "—"}</Box>
              </div>
            </ColumnLayout>

            {(runStatus.status === "uploading" || runStatus.status === "processing") && (
              <ProgressBar
                value={runStatus.status === "uploading" ? 5 : progress}
                additionalInfo={
                  runStatus.status === "uploading"
                    ? "Uploading file..."
                    : `Converting to SmartBiz format... ${progress}%`
                }
                status="in-progress"
              />
            )}

            {/* Pre-flight WebP detection notice — shown while conversion is in
                flight when the sample sniff found WebP images. Lets the seller
                know the longer runtime is expected and that the tool is
                handling it for them. */}
            {sniffResult && sniffResult.needs_conversion && (
              <Alert type="info">
                <strong>Heads up:</strong> Your catalog uses WebP images, which SmartBiz can't read directly.
                We're auto-converting them to JPEG and rehosting on our servers — your import will work without manual fixes.
                {sniffResult.projected_webp_conversions > 0 && (
                  <> Estimated <strong>{sniffResult.projected_webp_conversions}</strong> images will be converted in this run.</>
                )}
              </Alert>
            )}

            {runStatus.status === "processing" && (
              <ExpandableSection headerText="💻 Processing Logs" defaultExpanded={true}>
                <Box padding="s">
                  <pre className="listing-logs-container">{logs}</pre>
                </Box>
              </ExpandableSection>
            )}

            {runStatus.status === "completed" && runStatus.total_rows > 0 && (
              <SpaceBetween size="s" direction="horizontal">
                <Button
                  variant="primary"
                  iconName="download"
                  onClick={() => handleDownload(runStatus.run_id)}
                >
                  Download SmartBiz Excel
                </Button>
                <Button onClick={handleReset}>Convert Another File</Button>
              </SpaceBetween>
            )}

            {runStatus.status === "completed" && runStatus.total_rows === 0 && (
              <Alert type="warning">
                No products found in the uploaded file. Make sure it's a valid export from the selected platform.
              </Alert>
            )}

            {runStatus.status === "failed" && (
              <SpaceBetween size="s">
                <Alert type="error">{runStatus.error || "Conversion failed"}</Alert>
                <Button onClick={handleReset}>Try Again</Button>
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
}
